// Tenant-Admin/Subscription/BillingHistory.jsx
import React, { useState } from 'react';
import { FiClock, FiCheckCircle, FiPlusCircle, FiXCircle, FiTrendingUp, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const BillingHistory = ({ history }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  if (!history || history.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center flex flex-col items-center justify-center">
        <div className="p-3 bg-slate-50 text-slate-400 rounded-2xl mb-3">
          <FiClock className="w-6 h-6" />
        </div>
        <h3 className="text-sm font-bold text-slate-800">No History Available</h3>
        <p className="text-xs text-slate-400 mt-1 max-w-xs leading-normal">
          Your account history will show updates here as soon as you active your first workspace plan.
        </p>
      </div>
    );
  }

  // Helper mappings for icons
  const getActionIcon = (action) => {
    switch(action) {
      case 'renewed':
      case 'subscription_renewed':
        return <FiCheckCircle className="w-4 h-4 text-emerald-600" />;
      case 'upgraded':
        return <FiTrendingUp className="w-4 h-4 text-indigo-600" />;
      case 'created':
      case 'subscription_created':
        return <FiPlusCircle className="w-4 h-4 text-sky-600" />;
      case 'cancelled':
        return <FiXCircle className="w-4 h-4 text-rose-600" />;
      default:
        return <FiClock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'renewed': 'Plan Renewed',
      'subscription_renewed': 'Plan Renewed',
      'upgraded': 'Tier Upgraded',
      'created': 'Account Setup',
      'subscription_created': 'Account Setup',
      'subscription_updated': 'Settings Changed',
      'cancelled': 'Plan Ended'
    };
    return labels[action] || 'Update Logged';
  };

  const getStatusBadgeStyles = (action) => {
    if (action === 'cancelled') {
      return 'bg-rose-50 text-rose-700 border-rose-100';
    }
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  };

  // Pagination Logic math calculation
  const totalPages = Math.ceil(history.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = history.slice(startIndex, startIndex + itemsPerPage);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm flex flex-col">
      
      {/* Component Title Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
        <div>
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-wide">Account History Log</h2>
          <p className="text-[11px] text-slate-400 mt-0.5">Timeline of changes made to your workspace subscription status.</p>
        </div>
        <span className="text-xs font-bold text-slate-500 bg-slate-50 border border-slate-200/60 px-2 py-0.5 rounded-md">
          {history.length} {history.length === 1 ? 'record' : 'records'}
        </span>
      </div>
      
      {/* Table Display Component block */}
      <div className="w-full">
        {/* Hidden on desktop header row to enable seamless mobile stack parsing mapping */}
        <div className="hidden md:grid grid-cols-12 bg-slate-50 border-b border-slate-200 px-5 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          <div className="col-span-3">Timestamp</div>
          <div className="col-span-3">Action Completed</div>
          <div className="col-span-4">Details Description</div>
          <div className="col-span-2 text-right">Ledger Status</div>
        </div>

        <div className="divide-y divide-slate-100">
          {paginatedItems.map((record, index) => (
            <div 
              key={record.id || (startIndex + index)} 
              className="grid grid-cols-1 md:grid-cols-12 items-start md:items-center p-5 md:px-5 md:py-3.5 gap-2 md:gap-0 hover:bg-slate-50/50 transition animate-fadeIn"
            >
              {/* Item Column 1: Date and Time stamps */}
              <div className="col-span-3 text-xs">
                <span className="font-bold text-slate-700 block">
                  {new Date(record.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
                <span className="text-[11px] text-slate-400 block mt-0.5">
                  {new Date(record.date).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>

              {/* Item Column 2: Action details type */}
              <div className="col-span-3 flex items-center gap-2">
                <span className="p-1 bg-white rounded-md border border-slate-100 shadow-3xs flex-shrink-0">
                  {getActionIcon(record.action)}
                </span>
                <span className="text-xs font-bold text-slate-800">
                  {getActionLabel(record.action)}
                </span>
              </div>

              {/* Item Column 3: Summary details block description */}
              <div className="col-span-4 text-xs space-y-0.5">
                <p className="text-slate-600 font-medium leading-relaxed">{record.description}</p>
                {record.metadata?.planName && (
                  <p className="text-[10px] text-indigo-600 font-semibold bg-indigo-50/60 inline-block px-1.5 py-0.5 rounded">
                    Assigned Package: {record.metadata.planName}
                  </p>
                )}
              </div>

              {/* Item Column 4: System Verification state label */}
              <div className="col-span-2 flex md:justify-end items-center mt-1 md:mt-0">
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md border tracking-wide uppercase ${getStatusBadgeStyles(record.action)}`}>
                  {record.action === 'cancelled' ? 'Terminated' : 'Verified'}
                </span>
              </div>

            </div>
          ))}
        </div>
      </div>

      {/* Embedded Dynamic Pagination Footer Controls View */}
      {totalPages > 1 && (
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between text-xs">
          <span className="text-[11px] font-medium text-slate-400">
            Showing <span className="font-bold text-slate-700">{startIndex + 1}</span> to{' '}
            <span className="font-bold text-slate-700">
              {Math.min(startIndex + itemsPerPage, history.length)}
            </span>{' '}
            of <span className="font-bold text-slate-700">{history.length}</span> updates
          </span>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="p-1.5 border border-slate-200 bg-white rounded-lg text-slate-500 hover:bg-slate-50 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition"
              title="Previous Page"
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            
            <span className="text-[11px] font-bold text-slate-600 px-2 bg-slate-100 py-1 rounded-md">
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="p-1.5 border border-slate-200 bg-white rounded-lg text-slate-500 hover:bg-slate-50 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition"
              title="Next Page"
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default BillingHistory;